import { ErrorBox, Spinner, Title4 } from '@common/components';
import Alert from '@common/components/Alert/Alert';
import { PopperRow } from '@common/components/Popper';
import { AvailabilityForm } from '@features/DynamicPackagesConfigurationWizard/components/AvailabilityForm/AvailabilityForm';
import { useGetSpaPackageConfigGroups } from '@features/spa';
import { useIsWindowWiderThan } from '@common/hooks/useIsWindowWiderThan';
import { useTranslations } from '@lang';
import { ErrorWrapper } from '@features/checkout/pages/CheckoutBilling/components/BillingForm.styles';
import { gridTheme } from '@utils/grid';
import { some } from 'lodash';
import moment from 'moment';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { ConfigurationGroupType } from 'shared-types/SpaPackage';
import { findMissingIndexes } from '../../../helpers/findMissingIndexes';
import { PackagesFormValues, SelectedExperienceType } from '../../../types';
import {
  DividerLine,
  DropdownWrapper,
  SpaAvailabilityWrapper,
  TitleWrapper,
} from './SingleOptionExperienceTimeslotSelection.styles';
import { usePackageConfiguration } from '../../../contexts';

type Props = {
  experience: SelectedExperienceType;
  configurationGroups: ConfigurationGroupType[];
  index: number;
  experienceDate: string;
  guestIndex?: number;
};

export const SingleOptionExperienceTimeslotSelection: React.FC<Props> = ({
  experience,
  configurationGroups,
  index,
  experienceDate,
  guestIndex,
}) => {
  const { property, spaPackageId, spaOpenTime, spaCloseTime, totalGuestAmount } =
    usePackageConfiguration();
  const numberOfGuests = totalGuestAmount;

  const { watch, setValue } = useFormContext<PackagesFormValues>();
  const guestsHaveDifferentTimes = watch('experiences.guestsHaveDifferentTimes');
  const minimumTimeslotQuantity = guestsHaveDifferentTimes ? 1 : numberOfGuests;
  const { t } = useTranslations();
  const unavailableText = t('common.unavailable');
  const delayAfter = watch(`selectedExperiences.${index}.delayAfter`);
  const isMobile = !useIsWindowWiderThan(gridTheme.breakpoints.md);

  const {
    data: configGroups,
    isFetching,
    error,
  } = useGetSpaPackageConfigGroups(
    configurationGroups,
    property.id,
    Number(spaPackageId),
    moment(experienceDate).format('YYYY-MM-DD'),
    guestsHaveDifferentTimes ? 1 : numberOfGuests,
    spaOpenTime,
    spaCloseTime
  );

  const option = configGroups
    .flatMap((conf) => conf.options)
    .find((option) => option.id === experience.optionId);

  if (error) {
    return (
      <ErrorWrapper>
        <ErrorBox error={{ id: 'error.reservation_failed' }} />
      </ErrorWrapper>
    );
  }

  if (isFetching) {
    return <Spinner isActive={true} />;
  }

  if (!option) {
    return (
      <PopperRow>
        <Alert content={unavailableText} />
      </PopperRow>
    );
  }

  const currentGuestOptions = watch(
    `experiences.guestOptions.${guestIndex || guestIndex === 0 ? guestIndex : 0}.timeslots`
  );
  const emptyIndexes = findMissingIndexes(currentGuestOptions ?? []);

  if (some(emptyIndexes)) {
    emptyIndexes.forEach((i) => {
      setValue(
        `experiences.guestOptions.${
          guestIndex || guestIndex === 0 ? guestIndex : 0
        }.timeslots.${i}`,
        []
      );
    });
  }

  return (
    <>
      <SpaAvailabilityWrapper
        data-testid={`single-option-selection-guest-${guestIndex ?? 0}-exp-${experience.id}`}
      >
        <TitleWrapper>
          <Title4>{option.name}</Title4>
          <DividerLine />
        </TitleWrapper>
        <DropdownWrapper>
          {!isMobile && <div></div>}
          {!isFetching && (
            <AvailabilityForm
              id={experience.id}
              entityType={experience.type}
              timeslots={option.timeslots || []}
              namePrefix={`experiences.guestOptions.${
                guestIndex || guestIndex === 0 ? guestIndex : 0
              }.timeslots.${experience.dayNumber}.${index}`}
              guestIndex={guestIndex || guestIndex === 0 ? guestIndex : 0}
              delayAfter={delayAfter}
              dayNumber={experience.dayNumber}
              experienceIndex={index}
              minimumTimeslotQuantity={minimumTimeslotQuantity}
            />
          )}
        </DropdownWrapper>
      </SpaAvailabilityWrapper>
    </>
  );
};
